<template>
  <div id="astra-section">
    <section class="az-section--full az-hero__section">
      <div class="az-container__wrapper--full">
        <div class="az-container">
          <img
            src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Header+img-min.jpg"
            alt="Senior Person smiling"
          />
        </div>
      </div>
    </section>
    <section class="az-section az-section-a__bg az-padding__a">
      <div class="az-container__wrapper">
        <div class="az-container az-container-a az-hero-stagger">
          <CircleSvg />
          <h1 class="az-heading az-h1">
            High potassium: <br />Can it be a serious condition?
          </h1>
          <p class="az-lead">Here's a patient's perspective</p>
          <div class="az-txt__container az-hero__txt">
            <p>
              Yes, high potassium, also known as hyperkalemia, can be a serious
              condition.
            </p>
            <p>
              In honor of
              <span class="az-txt__shoutout"
                >National High Potassium Awareness Day</span
              >, here are the facts around hyperkalemia. Watch Mike's story to
              learn more about his journey with hyperkalemia.
            </p>
          </div>
          <div class="az-logo__container">
            <span>Paid for by</span>
            <div class="az-logo__share">
              <img
                src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/AstraZenecaLogo.svg"
                alt="AstraZeneca Logo"
              />
              <div class="az-share">
                <Share
                  :pageId="currentUrl"
                  webTitle="High potassium: Can it be a serious condition?"
                  context="ArticleMeta"
                  :format="{ design: 'default' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="az-section--full">
      <div class="az-container__wrapper--full">
        <div class="az-container">
          <div class="az-video-container">
            <div class="plyr__container">
              <div
                id="player"
                class="js-player"
                data-plyr-provider="youtube"
                data-plyr-embed-id="3Py4wJSa8Z8"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="az-section az-padding__b">
      <div class="az-container__wrapper">
        <div class="az-container">
          <div class="az-video__testimonial">
            <p class="az-txt__testimonial az-fade__up">
              "My goal for the future is learning to live with hyperkalemia"
            </p>
            <span class="az-testimonial__name az-fade__up"
              >Mike, patient with hyperkalemia</span
            >
          </div>
          <div class="az-container__img">
            <img
              class="az-clip__circle az-desktop"
              src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Circle-images_group.jpg"
              alt="Mikes Family smiling"
            />
            <img
              class="az-clip__circle az-mobile"
              src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Mike's+family_Mobile.jpg"
              alt="Mikes Family smiling"
            />
          </div>
        </div>
      </div>
    </section>
    <section class="az-section">
      <div class="az-container__wrapper">
        <div class="az-container">
          <div class="az-grid__group">
            <div
              v-for="(item, index) in 3"
              :key="index"
              class="az-grid__item az-fade__up"
              :class="{ 'az-is__collapsed': !activeItems[index] }"
              :ref="`gridItem${index}`"
            >
              <div class="az-grid-item__header">
                <p v-if="index === 0">What is hyperkalemia?</p>
                <p v-else-if="index === 1">
                  Which health conditions may lead to a higher chance of
                  developing hyperkalemia?
                </p>
                <p v-else>
                  What medications may lead to a higher chance of developing
                  hyperkalemia?
                </p>

                <button class="az-grid__collapse" @click="toggleItem(index)">
                  <span
                    class="az-collapse__icon az-collapse__a"
                    :class="{ rotated: !activeItems[index] }"
                  ></span>
                  <span class="az-collapse__icon az-collapse__b"></span>
                </button>
              </div>

              <slide-up-down
                :active="activeItems[index]"
                :duration="240"
                class="slide-container"
                :class="{ expanded: activeItems[index] }"
              >
                <!-- Content depending on index -->
                <div class="az-grid__item-content">
                  <!-- First item content -->
                  <div v-if="index === 0" class="az-grid__item--wrap">
                    <p>
                      Hyperkalemia, also known as high potassium, is a condition
                      in which the potassium (K<sup>+</sup>) levels in your
                      blood are higher than normal. It may not seem serious at
                      first, but can be an ongoing and life-threatening
                      condition if left untreated.
                    </p>
                    <p>
                      The fact is, your high potassium levels can recur, spike,
                      or remain high. This can cause complications, such as
                      heart issues, and can lead to hospitalizations.
                    </p>
                  </div>

                  <!-- Second item content -->
                  <div v-else-if="index === 1" class="az-grid__item--wrap">
                    <div class="az-grid__content-list__el">
                      <div class="az-list__el-icon">
                        <img
                          src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Lokelma+Reworked+icons_Kidneys.svg"
                          alt=""
                        />
                      </div>
                      <p>Certain kidney problems</p>
                    </div>
                    <div class="az-grid__content-list__el">
                      <div class="az-list__el-icon">
                        <img
                          src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Lokelma+Reworked+icons_Heart.svg"
                          alt=""
                        />
                      </div>
                      <p>Heart failure</p>
                    </div>
                    <div class="az-grid__content-list__el">
                      <div class="az-list__el-icon">
                        <img
                          src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Lokelma+Reworked+icons_Blood+Pressure.svg"
                          alt=""
                        />
                      </div>
                      <p>High blood pressure</p>
                    </div>
                    <div class="az-grid__content-list__el">
                      <div class="az-list__el-icon">
                        <img
                          src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Lokelma+Reworked+icons_Diabetes.svg"
                          alt=""
                        />
                      </div>
                      <p>Diabetes</p>
                    </div>
                  </div>

                  <!-- Third item content -->
                  <div v-else class="az-alt__collpase">
                    <div class="az-grid__item--wrap">
                      <div class="az-drop__mx">
                        <div class="az-drop__img">
                          <img
                            src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/pill+bottle.svg"
                            alt="pill bottle"
                            width="129"
                          />
                        </div>
                        <p>
                          Certain medications used to treat these conditions
                          (ACEi/ARB, MRA)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </slide-up-down>
            </div>
          </div>
          <div class="az-shape__container az-fade__up">
            <p>
              It's critical to have a discussion with your healthcare provider
              (HCP) about high potassium, especially if you have certain health
              conditions or are on certain medications.
            </p>
          </div>
        </div>
      </div>
    </section>
    <section class="az-section az-pd-section-top">
      <div class="az-container__wrapper">
        <div class="az-container">
          <div class="az-container__grid">
            <div class="az-col az-col__img">
              <img
                class="az-clip__circle"
                src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Annie.png"
                alt="Annie"
              />
              <div class="az-col az-col__txt">
                <p>
                  "When we first heard about the diagnosis of high potassium I
                  went to the doctor with him because I'm the one that asks
                  questions. We were sitting there chatting and I just said to
                  the doctor, 'Well what would happen if his potassium is too
                  high?'"
                </p>
                <span class="az-txt__name">Annie, Mike's wife</span>
              </div>
            </div>
            <div class="az-col az-col__content">
              <CircleSvg />
              <h2 class="az-h2">Learn about potassium levels</h2>
              <p>
                Potassium is a nutrient that helps cells work. The right levels
                of potassium are necessary to keep your muscles, nerves, and
                heart working well and are important for digestive and bone
                health.
              </p>
            </div>
          </div>
          <div class="az-flex__container">
            <div class="az-flex__item">
              <div class="az-shape__container az-shape__clock az-fade__up">
                <p>Move the arrow to learn about potassium levels</p>
              </div>
              <div class="az-potasium__lvl">
                <PotasiumClock />
                <p class="az-meq">mEq/L=milliequivalent per liter.</p>
              </div>
              <div class="az-txt__container">
                <p class="az-fade__up">
                  If left unmanaged, potassium levels can become dangerously
                  high. This may result in serious health issues, including
                  weakness, nausea, paralysis, and heart problems, such as
                  cardiac arrest. But there are things you can do to help keep
                  your potassium level under control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="az-section az-pd-section-top">
      <div class="az-container__wrapper">
        <div class="az-container">
          <div class="az-container__grid">
            <div class="az-col az-col__img">
              <img
                class="az-clip__circle"
                src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Mike.png"
                alt="Mike"
              />
              <div class="az-col az-col__txt">
                <p>
                  "The biggest changes I have had to make in my lifestyle have
                  been in diet"
                </p>
                <span class="az-txt__name">Mike</span>
              </div>
            </div>
            <div class="az-col az-col__content">
              <CircleSvg />
              <h2 class="az-h2">Potassium is found in many foods</h2>
              <p>
                Your healthcare provider (HCP) or a dietitian may help you plan
                your diet to make sure you are not consuming too much potassium
                through the foods and drinks you consume. But of course, it's
                important that you continue to maintain a healthy diet. As a
                general guide, here are some foods you may want to limit or
                avoid:
              </p>
            </div>
          </div>
          <div class="az-flex__container">
            <div class="az-flex__item az-fade__up">
              <PotasiumDrag />
            </div>
            <div class="az-footer az-fade__up">
              <p>
                If you are a patient,
                <a
                  href="https://www.lokelma.com/about-hyperkalemia.html"
                  rel="noopener noreferrer"
                  target="_blank"
                  >click here</a
                >
                to learn more about hyperkalemia
              </p>
              <p>
                If you are an HCP,
                <a
                  href="https://www.lokelma-hcp.com/about-hyperkalemia/hyperkalemia-risk"
                  rel="noopener noreferrer"
                  target="_blank"
                  >click here</a
                >
                to learn more about the risks associated with hyperkalemia
              </p>

              <div class="az-footer__logo">
                <img
                  src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/AstraZenecaLogo.svg"
                  alt="AstraZeneca Logo"
                />
              </div>
              <div class="az-footer__updt">
                <p>US-99472 Last Updated 4/25</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import Share from "./components/Share.vue";
import PotasiumClock from "./components/PotasiumClock.vue";
import PotasiumDrag from "./components/PotasiumDrag.vue";
import CircleSvg from "./components/CircleSvg.vue";
import { ref, onMounted } from "vue";
import SlideUpDown from "vue-slide-up-down";
import { useAnimations } from "./composables/useAnimations";
import { initViewportAnimations } from "./utils/animationUtils";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

// Current URL for sharing
const currentUrl = ref("");

// Track the open/closed state of each collapsible item - initially all open
const activeItems = ref([true, true, true]);

// Video player state
const isPlaying = ref(false);
let player = null;

// Function to toggle an item's open/closed state
function toggleItem(index) {
  activeItems.value[index] = !activeItems.value[index];
}

// Initialize and setup the Plyr player
function initPlayer() {
  // Create custom poster icon and template
  const posterConfig = {
    url: "https://labs.theguardian.com/2025/Astra+Zeneca/assets/Screenshot+2025-02-28+at+1.43.42%E2%80%AFPM-min.jpg",
    enabled: true,
  };

  // Initialize Plyr with custom settings
  player = new Plyr("#player", {
    controls: [
      "play-large",
      "play",
      "progress",
      "current-time",
      "mute",
      "volume",
      "fullscreen",
    ],
    youtube: {
      noCookie: true,
      rel: 0,
      showinfo: 0,
      iv_load_policy: 3,
      modestbranding: 1,
      // Add poster through YouTube API parameters
      start: 0,
      playsinline: 1,
    },
    previewThumbnails: {
      enabled: false,
    },
    // Intercept the poster to add our custom one
    posterLoaded: () => {
      updatePoster();
    },
    resetOnEnd: true,
  });

  // Set custom poster programmatically
  function updatePoster() {
    if (player) {
      const media = player.media;
      if (media) {
        const container = player.elements.container;

        // First make sure any old poster is removed
        const existingPosters = container.querySelectorAll(".custom-poster");
        existingPosters.forEach((poster) => poster.remove());

        // Create and append our custom poster
        if (posterConfig.enabled) {
          const poster = document.createElement("div");
          poster.classList.add("custom-poster");
          poster.style.backgroundImage = `url(${posterConfig.url})`;
          poster.addEventListener("click", () => {
            player.play();
          });

          // Insert the poster before the video
          container.appendChild(poster);
        }
      }
    }
  }

  // Listen for Plyr ready event
  player.on("ready", () => {
    console.log("Player is ready");
    updatePoster();
  });

  player.on("playing", () => {
    isPlaying.value = true;
    // Hide custom poster when playing
    const poster = player.elements.container.querySelector(".custom-poster");
    if (poster) {
      poster.style.display = "none";
    }
  });

  player.on("pause", () => {
    if (player.currentTime > 0.5) {
      isPlaying.value = true;
    } else {
      isPlaying.value = false;
      updatePoster();
    }
  });

  player.on("ended", () => {
    isPlaying.value = false;
    updatePoster();
  });
}

// Get GSAP and ScrollTrigger from composable
const { gsap, ScrollTrigger } = useAnimations();

onMounted(() => {
  // Initialize URL after component is mounted and window is available
  currentUrl.value = window.location.href;

  // Initialize video player
  initPlayer();

  // Initialize all animations based on classes
  initViewportAnimations(gsap, ScrollTrigger);
});
</script>

<style>
/* Plyr player styling */
.az-video-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.plyr__container {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
  transition: opacity 0.5s ease;
  opacity: 1;
}

.plyr,
iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Custom Plyr theme overrides */
:root {
  --plyr-color-main: #004d99;
  --plyr-video-control-color: #fff;
  --plyr-video-control-color-hover: #e3edfd;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.5s ease;
  z-index: 10;
}

.video-overlay img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.az-play__video {
  position: relative;
  z-index: 2;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.az-play__video:hover {
  transform: scale(1.1);
}

/* Button rotation when active */
.az-collapse__icon.rotated {
  transform: rotate(90deg);
}

/* Button interaction */
.az-grid__collapse {
  z-index: 10;
  transform: scale(1);
  transition: transform 0.16s ease;
}

.az-grid__collapse:hover {
  transform: scale(1.1);
}

.az-grid__collapse:active {
  transform: scale(0.95);
}

/* Make sure all grid items are the same height */
.az-grid__item {
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.24s ease;
}

/* Collapsed state for grid items */
.az-is__collapsed .az-collapse__icon.az-collapse__a {
  transform: rotate(90deg);
}

/* Make sure the content maintains flex layout when shown */
.az-grid__item-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

/* Style slide-up-down container */
.slide-container {
  transition: all 0.24s ease;
}

/* Expanded state */
.slide-container.expanded {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

/* Target the internal container of slide-up-down */
.slide-up-down-container {
  display: flex;
  flex-direction: column;
}

/* When parent is not collapsed, make slide-up-down grow to fill space */
.az-grid__item:not(.az-is__collapsed) .slide-up-down-container {
  flex-grow: 1;
}

/* When parent is collapsed, remove flex-grow */
.az-is__collapsed .slide-up-down-container {
  flex-grow: 0;
}

/* Ensure az-grid__item--wrap maintains flex grow */
.az-grid__item--wrap {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

/* Grid layout improvements to ensure equal heights */
.az-grid__group {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 25px;
  align-items: stretch;
}

/* Custom poster styling */
.custom-poster {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background-size: cover;
  background-position: center center;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-poster::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 108px;
  height: 108px;
  background-color: #e3edfd;
  border-radius: 50%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M8 5v14l11-7z' fill='%23004d99'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 48px;
}

.custom-poster:hover::after {
  background-color: #e3edfd;
  transform: translate(-50%, -50%) scale(1.1);
  transition: transform 0.2s ease;
}
</style>
